// End-to-end setup + lifecycle guide for a live, empty system.
// No demo logins: use your own Super Admin account and the users you invite.

export const WALKTHROUGH_INTRO =
  "This is the full lifecycle on a live system — there is no sample data and no demo logins. " +
  "Sign in with your own Super Admin account (or Microsoft SSO) and use the users you invite as you go. " +
  "Emails, SharePoint uploads and video rooms depend on your Microsoft 365 / Daily.co keys; if those " +
  "aren't connected the app degrades gracefully and shows the link or info on screen instead of sending.";

export const WALKTHROUGH = [
  {
    id: "wt-coordinators",
    role: "Super Admin",
    title: "1 — Invite your coordinators and give them their states",
    login: "Your Super Admin account",
    page: "Users",
    steps: [
      "Users → Invite User → Role = Campus Coordinator (or Campus Coordinator Supervisor). An @therapymgmt.com address is created as a Microsoft SSO invite with no password; anything else gets a temporary-password email.",
      "Edit the new coordinator and tick every state under 'States they are responsible for'. Save.",
      "Repeat for each coordinator. States can overlap — the first coordinator alphabetically owns a state when nobody is assigned directly.",
    ],
    expect:
      "Coordinators can sign in, and any school or placement request in one of their states is linked to them automatically from here on.",
  },
  {
    id: "wt-lists",
    role: "Campus Coordinator",
    title: "2 — Set up your dropdowns and the student checklist",
    login: "A Campus Coordinator (or Super Admin)",
    page: "Lists & Options, then Student Tasks",
    steps: [
      "Lists & Options → work through the five lists: Event types, Touchpoint types, Gift card choices, Rotation levels, and facility 'No' reasons. Add what you use, retire what you don't with the 'In use' switch.",
      "On an Event or Touchpoint type click Edit → 'Add question' to attach follow-up questions (Virtual Lunch & Learn → 'Which restaurant?' as a pick-list, marked Required).",
      "Student Tasks → New Task. The welcome-meeting task ships first in the list; add your own (complete paperwork, watch or read a link, acknowledge) and order them with the arrows.",
      "Set 'When does it open?' for each task: 0 days means it's waiting at their first login, 30 / 90 / 120 days schedules a check-in that many days after their rotation start date.",
      "Scope any task to specific disciplines and/or schools, or leave both empty so every student gets it.",
    ],
    expect:
      "Every dropdown in the app reflects your options, and every student who joins gets the same ordered checklist.",
  },
  {
    id: "wt-facilities",
    role: "Campus Coordinator",
    title: "3 — Tidy the facility directory",
    login: "A Campus Coordinator (or Super Admin)",
    page: "Facilities",
    steps: [
      "The facility directory syncs from SharePoint twice daily (Super Admin can force it: Settings → Sync from SharePoint).",
      "Click the eye-with-slash icon on any site that isn't participating — it's tagged 'Hidden' and disappears from every facility choice.",
      "Use 'Show hidden facilities' to review or un-hide them later.",
      "Open a facility → edit to set disciplines and 'Open slots per discipline' (capacity), and confirm the DOR contact.",
    ],
    expect:
      "Only real, participating facilities appear when a school or coordinator picks a site. Hidden ones never show up in a picker but keep any placement already running.",
  },
  {
    id: "wt-school",
    role: "Super Admin / Campus Coordinator",
    title: "4 — Add a school and its contacts",
    login: "Super Admin or a Campus Coordinator",
    page: "Schools, then Users",
    steps: [
      "Schools → + New School: name, state and the programs it offers, with contacts per program (Program Director, Fieldwork Coordinator/CE, Instructor, Career Services). Save.",
      "The coordinator who manages that state is assigned automatically — override it on the school if you want someone specific.",
      "Users → Invite User → Role = School User, Organization = that school, and set a Title.",
      "Back on the school → Edit → pick the Clinical Educator from the School Users dropdown, or click 'Auto-fill from School Users'.",
    ],
    expect:
      "The school exists, its contact can sign in, and it is linked to the right Campus Coordinator.",
  },
  {
    id: "wt-request",
    role: "School User",
    title: "5 — School submits a placement request",
    login: "The School User you just invited",
    page: "Placement Requests",
    steps: [
      "Placement Requests → + New Request. The rotation state pre-fills to the school's state; change it for out-of-state rotations.",
      "Pick the facility — every active, visible facility in that state is listed, whether or not a Clinical Instructor is on file yet.",
      "Fill in student name & email, discipline, graduation date, rotation level and dates, then Submit. Tick 'Student not yet known' if the student hasn't been chosen — an invite goes out later.",
    ],
    expect:
      "The request appears as 'Submitted' and is routed to the coordinator for that state. Schools only ever see their own requests.",
  },
  {
    id: "wt-assign",
    role: "Campus Coordinator",
    title: "6 — Coordinator matches and sends it to the facility",
    login: "The assigned Campus Coordinator",
    page: "Placement Requests → open the request",
    steps: [
      "Open the request and click Match Facilities to see visible sites in that state with open capacity for the discipline.",
      "Click 'Assign Facility'. The facility's Director of Rehab is emailed a one-click Yes/No link and notified in-app.",
      "Weekly reminders run until they answer, and the request escalates to a 'Non-Placement Alert' on your dashboard after the escalation window (Settings, default 5 business days).",
    ],
    expect:
      "Status becomes 'Awaiting Facility Response' and the DOR has everything they need to answer Yes or No.",
  },
  {
    id: "wt-decision",
    role: "DOR",
    title: "7 — The facility answers Yes or No",
    login: "The facility's DOR",
    page: "Placement Requests → open the request",
    steps: [
      "Sign in as the DOR (or use the Yes/No link in the email). The blue 'Action needed' banner shows 'Mark Yes / No' and 'Answer No'.",
      "Answering Yes: pick the supervising Clinical Instructor and confirm dates and welcome details. No CI on file yet? Add or invite one from the Clinical Instructors page first — that's expected, schools can request the site regardless.",
      "If the CI has filled out their bio, the report location, arrival time, dress code, parking and entrance auto-fill.",
      "Answering No: choose a reason (from the list you configured) — the request drops into the coordinator's 'Needs Re-routing' queue immediately.",
    ],
    expect:
      "A Yes shows as 'Facility Yes', assigns the Universal Paperwork Packet and emails the student a confirmation that includes their CI's bio. A No shows as 'Needs Re-routing' and alerts the coordinator.",
  },
  {
    id: "wt-student-tasks",
    role: "Student → Campus Coordinator",
    title: "8 — The student's first login: checklist and welcome meeting",
    login: "The student, then their coordinator",
    page: "Dashboard",
    steps: [
      "Sign in as the student. The dashboard opens with 'Your checklist' — tasks in order, later ones padlocked until the one above is done.",
      "On the meeting task click 'Set up my meeting': the assigned Campus Coordinator is shown by name and email. Pick a date and time and send it.",
      "Sign in as that coordinator: an orange banner counts the meetings waiting on you, and the 'Student meetings' card offers Confirm time, Change date/time and Cancel.",
      "Try 'Change date/time' — back on the student's dashboard the task reads 'Confirm New Time' and they can confirm it (or propose another; either side can propose, the other confirms).",
      "'Start Meeting' stays dimmed with a live countdown until 15 minutes before the confirmed time. Once it opens, either side can launch the Daily.co call — starting it completes the task and unlocks the next one.",
    ],
    expect:
      "Both dashboards show the same story, and every request, reschedule and confirmation is listed in the task's activity trail.",
  },
  {
    id: "wt-paperwork",
    role: "Campus Coordinator → Student",
    title: "9 — Paperwork and clearing the student to start",
    login: "Coordinator, then the student",
    page: "Templates → Paperwork Packets, placement detail, My Paperwork",
    steps: [
      "Templates → Paperwork Packets: manage the single Universal Paperwork Packet every student is assigned when a facility says Yes.",
      "On the placement's Paperwork section, add a one-off extra document for this student if needed, and use 'Open SharePoint Folder' for their secure folder.",
      "As the student, open 'My Paperwork', complete a form and upload a document, then submit each.",
      "Back as the coordinator, open each item and Approve, or Reject with a reason so the student can fix it.",
      "Once the facility has said Yes, a start date is set and every required item is Approved, click 'Clear to Start' — an Outlook invite goes to everyone.",
    ],
    expect:
      "Clearance only turns green when all three conditions are true, and the student can see exactly what's outstanding.",
  },
  {
    id: "wt-bios",
    role: "Everyone",
    title: "10 — Bios and the 'Your people' cards",
    login: "CI, DOR, coordinator, school contact",
    page: "My Bio → Dashboard",
    steps: [
      "Clinical Instructors, DORs, Campus Coordinators and School Users all have 'My Bio' in the sidebar (a banner nudges them until it's filled in). Add the answers and a photo, then Save.",
      "A CI's bio also feeds the welcome logistics — parking, entrance, arrival time, dress code — into every placement they precept.",
      "Coordinators can open Clinical Instructors → 'View / share bio' to print a bio or email it to a recruit.",
      "On any dashboard, the 'Your people' card lists the right contacts for that role — a student sees their CI, DOR, coordinator and school contact; a school sees every coordinator it works with. Click a name with a book icon to read the bio.",
    ],
    expect:
      "Everyone knows who they're working with before day one, and the bio content is reused in welcome emails and packets.",
  },
  {
    id: "wt-engagement",
    role: "School User → Campus Coordinator",
    title: "11 — Events, touchpoints and oversight",
    login: "School User, then coordinator / supervisor",
    page: "Request Event → Engagement, Touchpoints, Analytics, Audit Log",
    steps: [
      "As the School User, open 'Request Event'. The header confirms the school and state sent with the request; pick a type and answer any follow-up question (e.g. which restaurant). Send it.",
      "As the coordinator, Engagement → Events shows the request with the school, its state and the answers. Approve (and schedule it — Daily.co room + Outlook invites) or Decline with a reason.",
      "Touchpoints → 'By Student' to filter students and tick off touchpoints; the types and gift-card choices are the ones you configured in Lists & Options.",
      "Supervisors: the Dashboard's 'Coordinator Oversight' panel breaks down each coordinator's open requests, awaiting responses, alerts and touchpoint gaps — click a number to drill into the exact students.",
      "Super Admin: Audit Log records every sensitive action, and Users → eye icon lets you view the app exactly as another user sees it.",
    ],
    expect:
      "The supporting modules all reflect your own configuration, and the audit log shows the full history of everything above.",
  },
];

// ---------------------------------------------------------------------------
// DOR walkthrough — self-service onboarding for a Director of Rehab / manager.
// ---------------------------------------------------------------------------
export const DOR_WALKTHROUGH_INTRO =
  "This is everything you'll ever be asked to do in Campus Connections, in the order it happens. " +
  "You only have two jobs: answer Yes or No when a school asks to send you a student, and name the " +
  "Clinical Instructor who will precept them. Five minutes here and you're set for the year.";

export const DOR_WALKTHROUGH = [
  {
    id: "dor-signin",
    role: "Director of Rehab",
    title: "1 — Sign in with your work Microsoft account",
    login: "Your @therapymgmt.com email — no password to remember",
    page: "Sign in",
    steps: [
      "Open the link in your invite email and click 'Sign in with Microsoft'. Use the same work account you use for Outlook — there is no separate Campus Connections password.",
      "If you're already signed in to Microsoft 365, you'll land straight on your dashboard.",
      "Your dashboard is scoped to your facility: the requests waiting on you, everything you've already answered, your Clinical Instructors and your Campus Coordinator's contact details.",
    ],
    expect: "You reach your facility dashboard without creating or resetting any password.",
  },
  {
    id: "dor-find",
    role: "Director of Rehab",
    title: "2 — Find the request that's waiting on you",
    login: "Your DOR account",
    page: "Dashboard → Requests awaiting your Yes / No",
    steps: [
      "You'll get an email the moment a school requests a student at your site, plus a bell alert in the app. Both link straight to the request.",
      "On your dashboard, 'Requests awaiting your Yes / No' lists each one with the student, their discipline, the school and the requested rotation dates.",
      "Click 'Mark Yes / No' to open the full request.",
      "If it's been sitting a while, your Campus Coordinator may send you a reminder — it's the same request, not a new one.",
    ],
    expect: "Every request you need to decide on is in one list, and nothing reaches you by email only.",
  },
  {
    id: "dor-yes",
    role: "Director of Rehab",
    title: "3 — Answer Yes and name the Clinical Instructor",
    login: "Your DOR account",
    page: "Placement detail → Mark Yes / No",
    steps: [
      "Click 'Mark Yes / No' at the top of the request (or 'Respond' on the Facility Responses tab).",
      "Choose Accepted. Confirm the dates, or propose different start/end dates if those weeks don't work for you.",
      "Pick the Clinical Instructor who will precept the student. If the right person isn't listed, click 'Add a new instructor' right there — name, email, discipline — and they're invited and selected in one step.",
      "Add the first-day basics if you know them: where to report, arrival time, dress code, parking and entrance. Anything your CI has already saved in their bio fills in automatically.",
      "Save. The school and the Campus Coordinator are notified immediately, and the student gets their login and paperwork checklist.",
    ],
    expect: "The request shows Accepted, your CI is attached to it, and Campus Relations takes over the student's paperwork.",
  },
  {
    id: "dor-no",
    role: "Director of Rehab",
    title: "4 — Answer No without any awkward emails",
    login: "Your DOR account",
    page: "Placement detail → Answer No",
    steps: [
      "Click 'Answer No' and pick the reason — capacity full, no instructor available, staffing, dates unavailable, discipline not supported, and so on. Add a note if it helps.",
      "Save. The request goes back to the Campus Coordinator to re-route to another site; the school never chases you for an explanation.",
      "You can also decline straight from the decision link in your email if you're away from your desk.",
    ],
    expect: "The request leaves your list, the coordinator sees your reason, and you're not on the hook for finding an alternative.",
  },
  {
    id: "dor-capacity",
    role: "Director of Rehab",
    title: "5 — Set your capacity so you're asked the right amount",
    login: "Your DOR account",
    page: "Facilities → your site",
    steps: [
      "Open your facility and edit it. Set the disciplines you support and 'Open slots per discipline' — how many students you can take at once.",
      "Keep your Clinical Instructor list current: each one has a student capacity, so the system knows when you're full.",
      "If your site can't host at all for a while, tell your Campus Coordinator — they can hide the site so no school can pick it.",
    ],
    expect: "Coordinators and schools see realistic availability, so you get fewer requests you have to decline.",
  },
  {
    id: "dor-after",
    role: "Director of Rehab",
    title: "6 — What happens after you say Yes",
    login: "Your DOR account",
    page: "Dashboard → Everything you've answered",
    steps: [
      "Campus Relations collects and approves the student's documents. You don't review paperwork and you won't see the student's contact details until they're cleared — that's deliberate.",
      "When everything is approved you get a 'Student cleared for rotation' alert and email with the student's contact details, your CI's details and the confirmed dates, plus an Outlook invite for day one.",
      "Your CI is asked to finish the welcome packet (report location, arrival time, parking) which is emailed to the student.",
      "'Everything you've answered' keeps the full history of your Yes and No decisions, with each student's status.",
    ],
    expect: "You hear from the system exactly twice per student: when a decision is needed, and when they're cleared to start.",
  },
  {
    id: "dor-help",
    role: "Director of Rehab",
    title: "7 — Changes, cancellations and getting help",
    login: "Your DOR account",
    page: "Placement detail → Request Cancel, Messages",
    steps: [
      "Something changed and you can no longer host? Open the placement and click 'Request Cancel' with a reason — the Campus Coordinator handles the rest. A facility never cancels a student's rotation unilaterally.",
      "Need to swap the Clinical Instructor? Open the placement and assign a different one; the student is told who they'll be working with.",
      "Use Messages to reach your Campus Coordinator in the app — their name and email are on your dashboard too.",
      "Help Center → FAQs and Getting started answer the day-to-day questions, and this walkthrough is always here.",
    ],
    expect: "Every change goes through one place, and your coordinator is one click away.",
  },
];

export const walkthroughFor = (role) => {
  if (role === "DOR") return { intro: DOR_WALKTHROUGH_INTRO, steps: DOR_WALKTHROUGH };
  return { intro: WALKTHROUGH_INTRO, steps: WALKTHROUGH };
};
